/**
 * Composable for authentication and authorization
 * Centralizes token management, permissions, and RBAC logic.
 * Roles and permissions come from store (filled by /me); token is for identity only.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { decodeJWT } from '@/utils/common';

const TOKEN_KEY = 'srToken';

const getStoredToken = () =>
  typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;

// Shared reactive token state (singleton pattern)
const tokenValue = ref(getStoredToken());

// Update token when storage changes (e.g., login in another tab)
const handleStorageChange = (event) => {
  if (event.key === TOKEN_KEY) {
    tokenValue.value = event.newValue;
  }
};

// One listener for the whole app; ref count so we remove when last consumer unmounts
let storageListenerRefCount = 0;
const attachStorageListener = () => {
  storageListenerRefCount++;
  if (storageListenerRefCount === 1 && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorageChange);
  }
};
const detachStorageListener = () => {
  storageListenerRefCount--;
  if (storageListenerRefCount <= 0) {
    storageListenerRefCount = 0;
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', handleStorageChange);
    }
  }
};

/**
 * Composable for authentication and authorization
 * @returns {Object} Auth utilities and reactive state
 */
export function useAuth() {
  const store = useStore();

  onMounted(attachStorageListener);
  onBeforeUnmount(detachStorageListener);

  // Reactive token (updates when localStorage changes)
  const token = computed(() => tokenValue.value);

  // Decoded token payload (safe: invalid/corrupt token → null, no throw)
  const decodedToken = computed(() => {
    if (!token.value) return null;
    try {
      return decodeJWT(token.value);
    } catch (e) {
      return null;
    }
  });

  // Current user data from token (id, name, email, etc.)
  const currentUser = computed(() => decodedToken.value?.data || null);

  // User ID (token or store)
  const userId = computed(() => currentUser.value?.id ?? currentUser.value?.user_id ?? store.state.users?.user?.id ?? null);
  const departmentId = computed(
    () =>
      store.state.users?.user?.departmentId ??
      currentUser.value?.department_id ??
      currentUser.value?.departmentId ??
      null
  );

  // User roles: from store (source of truth from /me), fallback to token
  const userRoles = computed(() => {
    const fromStore = store.state.users?.user?.roles;
    if (fromStore && Array.isArray(fromStore)) return fromStore;
    const roles = currentUser.value?.roles;
    if (!roles || !Array.isArray(roles)) return [];
    return roles;
  });

  // User permissions: from store (filled by /me); token does not contain permissions
  const userPermissions = computed(() => {
    const fromStore = store.state.users?.user?.permissions;
    if (fromStore && Array.isArray(fromStore)) return fromStore;
    const perms = currentUser.value?.permissions;
    if (!perms || !Array.isArray(perms)) return [];
    return perms;
  });

  // Check if user has specific role
  const hasRole = (roleCode) => {
    return userRoles.value.some((role) => {
      if (typeof role === 'object' && role?.code) return role.code === roleCode;
      if (typeof role === 'string') return role === roleCode;
      return false;
    });
  };

  // Check if user has specific permission
  const hasPermission = (permissionCode) => {
    return userPermissions.value.some((p) => {
      if (typeof p === 'string') return p === permissionCode;
      if (typeof p === 'object' && p?.code) return p.code === permissionCode;
      return false;
    });
  };

  // Check if user is super admin
  const isSuperAdmin = computed(() => hasRole('super_admin'));

  /** Telegram broadcast compose/history (super_admin + dept_admin; API enforces scope). */
  const canManageTelegramNotifications = computed(
    () => hasRole('super_admin') || hasRole('dept_admin')
  );

  // Token expired? (no exp or past exp → expired)
  const isTokenExpired = computed(() => {
    if (!decodedToken.value?.exp) return true;
    return Date.now() >= decodedToken.value.exp * 1000;
  });

  // Authenticated = valid token, decoded, and not expired (avoids "logged in but 401" UX)
  const isAuthenticated = computed(
    () => !!token.value && !!decodedToken.value && !isTokenExpired.value
  );

  // Helper to check any of multiple permissions
  const hasAnyPermission = (...permissionCodes) => {
    return permissionCodes.some((code) => hasPermission(code));
  };

  // Helper to check all of multiple permissions
  const hasAllPermissions = (...permissionCodes) => {
    return permissionCodes.every((code) => hasPermission(code));
  };

  // Update token (e.g., after login/refresh). Prefer this over direct localStorage so current tab stays in sync.
  const setToken = (newToken) => {
    if (typeof window !== 'undefined') {
      if (newToken) {
        localStorage.setItem(TOKEN_KEY, newToken);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    tokenValue.value = newToken;
  };

  // Clear token (logout)
  const clearToken = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(TOKEN_KEY);
    }
    tokenValue.value = null;
  };

  return {
    // State
    token,
    decodedToken,
    currentUser,
    userId,
    departmentId,
    userRoles,
    userPermissions,
    isAuthenticated,
    isTokenExpired,
    isSuperAdmin,
    canManageTelegramNotifications,

    // Methods
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    setToken,
    clearToken,
  };
}

/**
 * Entities whose API gates do not follow the generic `<entity>.<action>.<scope>` codes.
 *
 * `api/v1/departments/manageDepartments.php` gates every mutation on `departments.manage.*`
 * (create/delete need the global one; status changes go through the `update` action), so the
 * seeded `departments.create.*` / `.delete.*` / `.status.*` codes are never checked server-side.
 */
const ENTITY_PERMISSION_OVERRIDES = {
  departments: {
    create: ['departments.manage.global'],
    update: ['departments.manage.global', 'departments.manage.department'],
    delete: ['departments.manage.global'],
    status: ['departments.manage.global', 'departments.manage.department'],
  },
};

/**
 * Create permission checkers for specific entity (reports, users, etc.)
 * Permission-only: no dept_admin role bypass. API enforces department scope.
 * @param {string} entity - Entity name (e.g., 'reports', 'users')
 * @returns {Object} Permission checkers for the entity
 */
export function useEntityPermissions(entity) {
  const { isSuperAdmin, hasPermission } = useAuth();
  const overrides = ENTITY_PERMISSION_OVERRIDES[entity] || {};

  const allows = (action, defaultCodes) => {
    if (isSuperAdmin.value) return true;
    return (overrides[action] || defaultCodes).some((code) => hasPermission(code));
  };

  const canCreate = computed(() =>
    allows('create', [`${entity}.create.global`, `${entity}.create.department`])
  );

  const canRead = computed(() =>
    allows('read', [
      `${entity}.view.global`,
      `${entity}.view.department`,
      `${entity}.read.global`,
      `${entity}.read.department`,
    ])
  );

  const canUpdate = computed(() =>
    allows('update', [`${entity}.update.global`, `${entity}.update.department`])
  );

  const canDelete = computed(() =>
    allows('delete', [`${entity}.delete.global`, `${entity}.delete.department`])
  );

  const canStatusChange = computed(() =>
    allows('status', [`${entity}.status.global`, `${entity}.status.department`])
  );

  const hasGlobalAccess = computed(() =>
    isSuperAdmin.value ||
    hasPermission(`${entity}.view.global`) ||
    hasPermission(`${entity}.read.global`) ||
    hasPermission(`${entity}.update.global`)
  );

  return {
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    canStatusChange,
    hasGlobalAccess,
  };
}

export default useAuth;
